import { reactive, computed } from 'vue';

// Scope shared by every visual on the Tasks tab.
//   teamIds / taskIds   what the user picked in the S-curve's team / task selectors
//   lockedTeamIds       permission scope: the current user's teams. Every query is restricted to
//                       tasks whose sub-tasks are led by one of these teams, before any selection
//                       is applied. null = no lock (administrators see the whole schedule);
//                       [] = the user is in no team, which leaves nothing in scope.
//   scopeResolved       false until the permission scope has been worked out; queries wait for it
//   statuses            cross-filter set by clicking a Current tasks bucket (task status keys)
//   drawingSetIds /     cross-filter set by clicking a row of the drawing set hierarchy
//   drawingIds
//   workload            cross-filter set by clicking a Workload bar: { side, bin, step } = the
//                       tasks completed in (past) / due in (next) that period
//   recentTaskIds       cross-filter set by clicking a Recent activity row (that one task)
//   waitingTaskIds      cross-filter set by clicking an Others-waiting-on-us row (that one task)
//   detailsTaskIds      cross-filter set by clicking a Task details row (that one task)
// A visual that owns a cross-filter dimension queries with that dimension ignored, so it keeps
// showing every bucket / row and the selection can be changed or cleared.
export const taskSCurveFilters = reactive({
  teamIds: [],
  taskIds: [],
  lockedTeamIds: null,
  scopeResolved: false,
  statuses: [],
  drawingSetIds: [],
  drawingIds: [],
  workload: null,
  recentTaskIds: [],
  waitingTaskIds: [],
  detailsTaskIds: [],
});

/** The user narrowed the scope with the selectors. */
export const isTaskSCurveFiltered = computed(
  () => taskSCurveFilters.teamIds.length > 0 || taskSCurveFilters.taskIds.length > 0
);

/** A visual's click selection is narrowing the other visuals. */
export const isTaskCrossFiltered = computed(
  () => taskSCurveFilters.statuses.length > 0
    || taskSCurveFilters.drawingSetIds.length > 0
    || taskSCurveFilters.drawingIds.length > 0
    || taskSCurveFilters.workload !== null
    || taskSCurveFilters.recentTaskIds.length > 0
    || taskSCurveFilters.waitingTaskIds.length > 0
    || taskSCurveFilters.detailsTaskIds.length > 0
);

/** Anything the user did that narrows the scope (drives the "filtered" chips). */
export const isTaskFiltered = computed(() => isTaskSCurveFiltered.value || isTaskCrossFiltered.value);

/** The permission scope has been resolved, so queries may run. */
export const isTaskScopeReady = computed(() => taskSCurveFilters.scopeResolved);

/** Something narrows the scope, so the prebuilt (unfiltered) s_curve table can't be used. */
export const isTaskScopeRestricted = computed(
  () => isTaskFiltered.value || taskSCurveFilters.lockedTeamIds !== null
);

/**
 * The scope object every SQL builder in taskTables.js takes. `ignore` names the cross-filter
 * dimensions ('status', 'drawing', 'workload', 'recent', 'waiting', 'details') the caller must not apply
 * to itself.
 */
export const taskScope = (ignore = []) => ({
  teamIds: taskSCurveFilters.teamIds,
  taskIds: taskSCurveFilters.taskIds,
  lockedTeamIds: taskSCurveFilters.lockedTeamIds,
  statuses: taskSCurveFilters.statuses,
  drawingSetIds: taskSCurveFilters.drawingSetIds,
  drawingIds: taskSCurveFilters.drawingIds,
  workload: taskSCurveFilters.workload,
  recentTaskIds: taskSCurveFilters.recentTaskIds,
  waitingTaskIds: taskSCurveFilters.waitingTaskIds,
  detailsTaskIds: taskSCurveFilters.detailsTaskIds,
  ignore,
});

/** Everything that should trigger a re-query, as one string for a watcher. */
export const taskScopeKey = computed(() => JSON.stringify([
  taskSCurveFilters.teamIds,
  taskSCurveFilters.taskIds,
  taskSCurveFilters.lockedTeamIds,
  taskSCurveFilters.scopeResolved,
  taskSCurveFilters.statuses,
  taskSCurveFilters.drawingSetIds,
  taskSCurveFilters.drawingIds,
  taskSCurveFilters.workload,
  taskSCurveFilters.recentTaskIds,
  taskSCurveFilters.waitingTaskIds,
  taskSCurveFilters.detailsTaskIds,
]));

/**
 * Apply the permission scope. Pass the user's team ids to lock the tab to them, or null for
 * no lock (administrators). A selected team the user isn't a member of is dropped.
 */
export const setLockedTeams = (teamIds) => {
  if (teamIds === null) {
    taskSCurveFilters.lockedTeamIds = null;
  } else {
    const ids = [...new Set(teamIds.map(String))];
    taskSCurveFilters.lockedTeamIds = ids;
    taskSCurveFilters.teamIds = taskSCurveFilters.teamIds.filter(id => ids.includes(String(id)));
  }
  taskSCurveFilters.scopeResolved = true;
};

/** Click a Current tasks bucket: select that status, or clear it when it is already selected. */
export const toggleStatusFilter = (status) => {
  taskSCurveFilters.statuses = taskSCurveFilters.statuses[0] === status ? [] : [status];
};

/** Click a drawing set or a drawing in the hierarchy: one selection at a time across both levels. */
export const toggleDrawingFilter = ({ drawingSetId = null, drawingId = null } = {}) => {
  if (drawingSetId !== null) {
    const same = taskSCurveFilters.drawingSetIds[0] === drawingSetId;
    taskSCurveFilters.drawingSetIds = same ? [] : [drawingSetId];
    taskSCurveFilters.drawingIds = [];
  } else if (drawingId !== null) {
    const same = taskSCurveFilters.drawingIds[0] === drawingId;
    taskSCurveFilters.drawingIds = same ? [] : [drawingId];
    taskSCurveFilters.drawingSetIds = [];
  }
};

/** Click a Workload bar: the tasks completed in (past) or due in (next) that period. */
export const toggleWorkloadFilter = ({ side, bin, step }) => {
  const cur = taskSCurveFilters.workload;
  taskSCurveFilters.workload = cur && cur.side === side && cur.bin === bin && cur.step === step
    ? null
    : { side, bin, step };
};

/** Click a Recent activity row: that one task. */
export const toggleRecentTaskFilter = (taskId) => {
  taskSCurveFilters.recentTaskIds = taskSCurveFilters.recentTaskIds[0] === taskId ? [] : [taskId];
};

/** Click an Others-waiting-on-us row: that one task. */
export const toggleWaitingTaskFilter = (taskId) => {
  taskSCurveFilters.waitingTaskIds = taskSCurveFilters.waitingTaskIds[0] === taskId ? [] : [taskId];
};

/** Click a Task details row: that one task. */
export const toggleDetailsTaskFilter = (taskId) => {
  taskSCurveFilters.detailsTaskIds = taskSCurveFilters.detailsTaskIds[0] === taskId ? [] : [taskId];
};

export const clearCrossFilters = () => {
  taskSCurveFilters.statuses = [];
  taskSCurveFilters.drawingSetIds = [];
  taskSCurveFilters.drawingIds = [];
  taskSCurveFilters.workload = null;
  taskSCurveFilters.recentTaskIds = [];
  taskSCurveFilters.waitingTaskIds = [];
  taskSCurveFilters.detailsTaskIds = [];
};
